import { useId, useState } from 'react';
import type { BrawdleSuggestion } from '../../../shared/types/api';

type AutocompleteInputProps = {
  value: string;
  suggestions: BrawdleSuggestion[];
  loading: boolean;
  disabled?: boolean;
  label: string;
  placeholder: string;
  loadingText: string;
  emptyText: string;
  onChange: (nextValue: string) => void;
  onSelect: (suggestion: BrawdleSuggestion) => void;
  onSubmit: () => void;
};

export const AutocompleteInput = ({
  value,
  suggestions,
  loading,
  disabled = false,
  label,
  placeholder,
  loadingText,
  emptyText,
  onChange,
  onSelect,
  onSubmit,
}: AutocompleteInputProps) => {
  const inputId = useId();
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});
  const showDropdown = !disabled && (loading || suggestions.length > 0);

  return (
    <div className="w-full">
      <label htmlFor={inputId} className="mb-2 block text-sm font-medium text-white">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full rounded-xl border border-white/40 bg-white/15 px-4 py-3 text-base text-white outline-none caret-white transition-colors placeholder:text-white/70 focus:border-white disabled:opacity-60"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSubmit();
            }
          }}
        />
        {showDropdown && (
          <div className="absolute left-0 right-0 top-full z-10 mt-2 rounded-xl border border-white/30 bg-white/10 shadow-xl backdrop-blur">
            {loading && <div className="px-4 py-3 text-sm text-white">{loadingText}</div>}
            {!loading && suggestions.length === 0 && (
              <div className="px-4 py-3 text-sm text-white">{emptyText}</div>
            )}
            {!loading && suggestions.length > 0 && (
              <ul className="max-h-60 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <li key={suggestion.slug}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-white hover:bg-white/15 focus-visible:bg-white/20 focus-visible:outline-none"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onSelect(suggestion)}
                    >
                      {suggestion.imagePath && !brokenImages[suggestion.slug] ? (
                        <img
                          src={suggestion.imagePath}
                          alt={suggestion.name}
                          className="h-10 w-10 rounded-full border border-white/30 bg-white/10 object-cover"
                          onError={() =>
                            setBrokenImages((prev) => ({
                              ...prev,
                              [suggestion.slug]: true,
                            }))
                          }
                        />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-base font-semibold uppercase text-white">
                          {suggestion.name.slice(0, 1)}
                        </span>
                      )}
                      <span className="text-base font-medium text-white">{suggestion.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

